import React, { useEffect, useRef, useState } from 'react'
import './TerminalIntro.css'
import { useEffects } from '../contexts/EffectsContext'

const PIPELINE = 'analyzed >> designed >> built >> shipped'
const ROLE_COMMAND = 'martin --role'
const ROLES = [
	'SPFx web parts & extensions',
	'Microsoft Teams apps',
	'Azure integrations',
	'TypeScript & .NET',
	'CI/CD · K8s · GitOps'
]

const TYPE_MS = 55
const ERASE_MS = 28
const HOLD_MS = 1800

const SECTION_TARGETS: Record<string, string> = {
	skills: 'section-skillsMatrix',
	architecture: 'section-architecture',
	timeline: 'section-careerTimeline',
	demos: 'section-spfxShowcase',
	chat: 'section-teamsIntegration',
	talk: 'section-teamsIntegration',
}

const HELP_TEXT = 'commands: skills · architecture · timeline · demos · chat · talk · contact · cv · whoami · clear · exit'

// Tab-completable commands (incl. the easter egg — tab away)
const COMMANDS = [
	'help', 'skills', 'architecture', 'timeline', 'demos',
	'chat', 'talk', 'contact', 'cv', 'whoami', 'sudo hire-martin', 'clear', 'exit'
]

const longestCommonPrefix = (items: string[]): string => {
	if (items.length === 0) return ''
	let prefix = items[0]
	for (const item of items.slice(1)) {
		while (!item.startsWith(prefix)) {
			prefix = prefix.slice(0, -1)
		}
	}
	return prefix
}

interface HistoryLine {
	kind: 'cmd' | 'out'
	text: string
}

/**
 * Terminal-style intro. Demo mode types the pipeline line and loops
 * through roles (or renders statically with effects off). Clicking the
 * terminal switches to an interactive mini-CLI — the easter egg for
 * visitors who think like developers.
 */
export const TerminalIntro: React.FC = () => {
	const { effects } = useEffects()
	const animate = effects.typewriter

	const [mode, setMode] = useState<'demo' | 'cli'>('demo')
	const [pipelineText, setPipelineText] = useState('')
	const [roleText, setRoleText] = useState('')
	const [pipelineDone, setPipelineDone] = useState(false)
	const [history, setHistory] = useState<HistoryLine[]>([])
	const [input, setInput] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)
	const bodyRef = useRef<HTMLDivElement>(null)

	// Display values are derived: with the typewriter off (or in CLI mode)
	// the full text renders directly — no state round-trips needed.
	const isCli = mode === 'cli'
	const shownPipeline = !animate || isCli ? PIPELINE : pipelineText
	const shownDone = !animate || isCli || pipelineDone
	const shownRole = animate ? roleText : ROLES[0]

	// Phase 1 (demo): type the pipeline line once.
	useEffect(() => {
		if (!animate || mode === 'cli') return
		let i = 0
		let timer: ReturnType<typeof setTimeout>
		const tick = () => {
			i += 1
			setPipelineText(PIPELINE.slice(0, i))
			if (i < PIPELINE.length) {
				timer = setTimeout(tick, TYPE_MS)
			} else {
				setPipelineDone(true)
			}
		}
		// reset inside the first scheduled callback, then start typing
		timer = setTimeout(() => {
			setPipelineText('')
			setRoleText('')
			setPipelineDone(false)
			timer = setTimeout(tick, 350)
		}, 50)
		return () => clearTimeout(timer)
	}, [animate, mode])

	// Phase 2 (demo): loop roles (type, hold, erase).
	useEffect(() => {
		if (!animate || !pipelineDone || mode === 'cli') return
		let roleIndex = 0
		let charIndex = 0
		let erasing = false
		let timer: ReturnType<typeof setTimeout>

		const tick = () => {
			const role = ROLES[roleIndex]
			if (!erasing) {
				charIndex += 1
				setRoleText(role.slice(0, charIndex))
				if (charIndex < role.length) {
					timer = setTimeout(tick, TYPE_MS)
				} else {
					erasing = true
					timer = setTimeout(tick, HOLD_MS)
				}
			} else {
				charIndex -= 1
				setRoleText(role.slice(0, charIndex))
				if (charIndex > 0) {
					timer = setTimeout(tick, ERASE_MS)
				} else {
					erasing = false
					roleIndex = (roleIndex + 1) % ROLES.length
					timer = setTimeout(tick, 350)
				}
			}
		}
		timer = setTimeout(tick, 600)
		return () => clearTimeout(timer)
	}, [animate, pipelineDone, mode])

	// CLI: keep the latest output in view (DOM sync, no state).
	useEffect(() => {
		if (mode === 'cli' && bodyRef.current) {
			bodyRef.current.scrollTop = bodyRef.current.scrollHeight
		}
	}, [history, mode])

	const enterCli = () => {
		if (mode !== 'cli') {
			setMode('cli')
			setHistory([{ kind: 'out', text: 'interactive mode — type \'help\'' }])
		}
		// Focus on the next frame so the input exists.
		requestAnimationFrame(() => inputRef.current?.focus())
	}

	const scrollToSection = (id: string) => {
		document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	// Bash-style Tab completion: single match fills, multiple matches
	// extend to the common prefix or list the options.
	const completeCommand = () => {
		const current = input.trimStart().toLowerCase()
		const matches = COMMANDS.filter(command => command.startsWith(current))
		if (matches.length === 0) return
		if (matches.length === 1) {
			setInput(matches[0])
			return
		}
		const prefix = longestCommonPrefix(matches)
		if (prefix.length > current.length) {
			setInput(prefix)
		} else {
			setHistory(prev => [...prev, { kind: 'out', text: matches.join('  ') }])
		}
	}

	const execute = (raw: string) => {
		const command = raw.trim().toLowerCase()
		const out: HistoryLine[] = [{ kind: 'cmd', text: raw }]

		if (command === '') {
			// just an empty prompt line
		} else if (command === 'help') {
			out.push({ kind: 'out', text: HELP_TEXT })
		} else if (command === 'chat' || command === 'talk') {
			out.push({ kind: 'out', text: `opening CV ${command}…` })
			scrollToSection(SECTION_TARGETS[command])
			document.dispatchEvent(new CustomEvent('cv:open-tab', { detail: { mode: command === 'talk' ? 'voice' : 'chat' } }))
		} else if (Object.prototype.hasOwnProperty.call(SECTION_TARGETS, command)) {
			out.push({ kind: 'out', text: `navigating to ${command}…` })
			scrollToSection(SECTION_TARGETS[command])
		} else if (command === 'contact') {
			out.push({ kind: 'out', text: 'opening mail client…' })
			window.location.href = 'mailto:martin@petvalsky.com'
		} else if (command === 'cv') {
			out.push({ kind: 'out', text: 'rendering print view…' })
			window.print()
		} else if (command === 'whoami') {
			out.push({ kind: 'out', text: 'guest — potential colleague, client… or both' })
		} else if (command === 'sudo hire-martin') {
			out.push({ kind: 'out', text: 'permission granted ✓ — use \'contact\' to proceed' })
		} else if (command === 'clear') {
			setHistory([])
			setInput('')
			return
		} else if (command === 'exit') {
			setHistory([])
			setInput('')
			setMode('demo')
			return
		} else {
			out.push({ kind: 'out', text: `command not found: ${command} — try 'help'` })
		}

		setHistory(prev => [...prev, ...out])
		setInput('')
	}

	return (
		<div
			className="terminal-intro"
			onClick={enterCli}
			role="group"
			aria-label="Interactive terminal — click and type 'help'"
		>
			<div className="terminal-intro-bar">
				<span className="terminal-intro-dot terminal-intro-dot-red" />
				<span className="terminal-intro-dot terminal-intro-dot-yellow" />
				<span className="terminal-intro-dot terminal-intro-dot-green" />
				<span className="terminal-intro-title">martin@petvalsky.com</span>
			</div>
			<div className="terminal-intro-body" ref={bodyRef}>
				<div className="terminal-intro-line">
					<span className="terminal-intro-prompt">$&nbsp;</span>
					<span>{shownPipeline}</span>
					{/* !shownDone already implies animate && demo mode */}
					{!shownDone && <span className="terminal-intro-cursor" />}
				</div>

				{mode === 'demo' && shownDone && (
					<>
						<div className="terminal-intro-line">
							<span className="terminal-intro-prompt">$&nbsp;</span>
							<span className="terminal-intro-command">{ROLE_COMMAND}&nbsp;</span>
							<span className="terminal-intro-role">{shownRole}</span>
							{animate && <span className="terminal-intro-cursor" />}
						</div>
						<div className="terminal-intro-hint">{'// click here and type \'help\''}</div>
					</>
				)}

				{mode === 'cli' && (
					<>
						{history.map((line, index) => (
							<div key={index} className="terminal-intro-line">
								{line.kind === 'cmd'
									? (
										<>
											<span className="terminal-intro-prompt">$&nbsp;</span>
											<span className="terminal-intro-command">{line.text}</span>
										</>
									)
									: <span className="terminal-intro-output">{line.text}</span>}
							</div>
						))}
						<div className="terminal-intro-line">
							<span className="terminal-intro-prompt">$&nbsp;</span>
							<input
								ref={inputRef}
								className="terminal-intro-input"
								value={input}
								onChange={event => setInput(event.target.value)}
								onKeyDown={event => {
									if (event.key === 'Enter') execute(input)
									if (event.key === 'Tab') {
										event.preventDefault()
										completeCommand()
									}
								}}
								aria-label="Terminal command input — type 'help' for available commands"
								autoComplete="off"
								autoCapitalize="off"
								spellCheck={false}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default TerminalIntro
